import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { alertService } from '@app/modules/common/alert';
import { languageService, languageSupport } from '@app/modules/common/language';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

import { Place } from '@app/modules/model/place';
import { Extra } from '@app/modules/model/extra';
import { User } from '@app/modules/model/user';
import { QrCode } from '@app/modules/model/qrcode';
import { Product } from '@app/modules/model/product';
import { ExtraPeriod } from '@app/modules/model/period';
import { ExtraProduct } from '@app/modules/model/extraproduct';
import { ExtraQrCode } from '@app/modules/model/extratable';

/************************************/
/* OFFER COMPONENT                  */
/************************************/

@Component({
    selector: 'ui-extra-edit',
    templateUrl: 'ui-edit.html',
    styleUrls: [
      'ui-edit.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiExtraEdit extends languageSupport implements OnInit, OnChanges, OnDestroy {    
    @Input('place') _place: Place = null; 
    @Input('extra') _extra: Extra = null; 
    @Input('user') _user: User = null; 

    get CanModify(){
        return this._place.AllowExtraManage(this._user);
    }

    get CanRemove(){
        if (this._place.AllowExtraManage(this._user)){
            if (this._extra.IsValid && !this._extra.ToInsert){
                return true;
            }
        }
        return false;         
    }

    /**********************************/
    /* EXTRA VIEW                     */
    /**********************************/

    private _nophoto = '/assets/image/extra.png';

    private get _ExtraPeriods(){
        let _periods = [];
        for(let _period of this._extra.periods){
            if (_period.IsValid){
                _periods.push(_period.Copy());
            }
        }
        return _periods;
    }

    private get _ExtraQrCodes(){
        let _qrcodes = [];
        for(let _qrcode of this._extra.tables){
            if ((_qrcode.IsValid)){
                _qrcodes.push(_qrcode.qrcode);
            }
        }

        _qrcodes.sort((a, b) => {
            return a.number - b.number;
        });

        return _qrcodes;
    }

    private get _ExtraProducts(){
        let _products = [];
        for(let _product of this._extra.products){
            if (_product.IsValid){
                _products.push(_product);
            }
        }
        return _products;
    }
   
    private _extraForm = null;   
    get ExtraForm(){
        if (this._extraForm == null){
            this.LoadExtraForm();
        }
        return this._extraForm;
    }

    private ValidatePeriods(control: FormControl) {
        let _isvalid = control.value.every(period => {
            return period.IsValid;
        })

        return _isvalid ? null : {'period': {valid: false}};
    }

    OnAlldayChange(){
        let _control = this.ExtraForm.get('periods');
        if (_control){
            if (this.ExtraForm.get('allday').value){
                _control.clearValidators();
                _control.setErrors(null);
                _control.updateValueAndValidity();
            }
            else {
                _control.setValidators([ Validators.required, this.ValidatePeriods.bind(this) ]);
                _control.setErrors(null);
                _control.updateValueAndValidity();
            }            
        }
    }

    OnAllprdChange(){
        let _control = this.ExtraForm.get('products');
        if (_control){
            if (this.ExtraForm.get('allprd').value){
                _control.clearValidators();
                _control.setErrors(null);
                _control.updateValueAndValidity();
            }
            else {
                _control.setValidators([ Validators.required ]);
                _control.setErrors(null);
                _control.updateValueAndValidity();
            }            
        }
    }

    private LoadExtraForm(){
        let _photo = this._extra.avatar || this._nophoto;
        let _name = this._extra.name;
        let _type = this._extra.type;
        let _price = this._extra.price;
        let _description = this._extra.description
        let _allday = this._extra.allday;
        let _periods = this._ExtraPeriods;      // make a copy of the periods
        let _tables = this._ExtraQrCodes;       // make a copy of the tables
        let _allprd = this._extra.allprd;
        let _products = this._ExtraProducts;    // make a copy of the products

        if (this._extraForm == null){
            this._extraForm = new FormGroup({
                photo: new FormControl(_photo, [
                    // no validators required
                ]),
                name: new FormControl(_name, [ 
                    Validators.required 
                ]),
                type: new FormControl(_type, [
                    Validators.required
                ]),
                price: new FormControl(_price, [ 
                    Validators.required 
                ]),
                description: new FormControl(_description, [
                    // no validators
                ]),
                allday: new FormControl(_allday, [
                    // no validators
                ]),
                periods: new FormControl(_periods, [
                    // no validators
                ]),
                tables: new FormControl(_tables, [
                    Validators.required
                ]),
                allprd: new FormControl(_allprd, [
                    // no validators
                ]),
                products: new FormControl(_products, [
                    // no validators
                ])
            });
        }
        else {
            this._extraForm.patchValue({
                photo: _photo,
                name: _name,
                type: _type,
                price: _price,
                description: _description,
                allday: _allday,
                periods: _periods,
                tables: _tables,
                allprd: _allprd,
                products: _products
            });

            this.OnTypeChange();
        }

        this._extraForm.markAsPristine();
        setTimeout(() => {
            this._extraForm.markAsPristine();
        }, 0);
    }

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    private _CanClose(){
        return new Promise(async (resolve) => {
            let _form = this.ExtraForm;
            if (_form.dirty){
                console.log("[EDIT EXTRA] confirm changes lost");
                const alert = this.alertCtrl.alert({
                    header: this.lang.tr('@changes_lost_confirm_tittle'),
                    message: this.lang.tr('@changes_lost_confirm_message'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.lang.tr('@yes'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(true);
                            }, 100);
    
                            return false;
                        }
                    }, {
                        text: this.lang.tr('@no'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(false);
                            }, 100);
    
                            return false;
                        }
                    }]
                });
    
                alert.onDidDismiss().then((detail:any) => {
                    if (detail.data){
                        _form.markAsPristine();
                    }
                    
                    resolve(detail.data);
                });
    
                await alert.present();        
            }
            else {
                resolve(true);
            }
        });
    }

    private _close_subscription = null;
    private _theme_subscription = null;

    ngOnInit(){
        this._close_subscription = this.view.OnCloseRequest.subscribe(
        async () => {
            this.view.OnCloseResponse(await this._CanClose());
        });

        this._theme_subscription = this.view.OnTheme.subscribe(
        data => {
            this.change.markForCheck();
        });
    }

    private _subscriptions = [];
    ngOnChanges(){
        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];

        if (this._extra){
            this._subscriptions.push(
                this._extra.OnRefresh.subscribe(
                data => {
                    this.change.markForCheck();
                })
            )
        }

        this.LoadExtraForm();
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        if (this._theme_subscription){
            this._theme_subscription.unsubscribe();
            this._theme_subscription = null;
        }

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    @Output('onPanel') _onPanel = new EventEmitter<any>();     
    private _OnClose(){
        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        if (this.view.RightPanel){
            this._onPanel.emit({
                scnd: null,
                args: null
            });    
        }
        else {
            this._onPanel.emit({
                scnd: null
            });

            // wait for animation to finish
            setTimeout(() => {
                this._onPanel.emit({
                    args: null
                });    
            }, 300);
        }
    }

    OnTypeChange(){
        let _allprd = this.ExtraForm.get('allprd');
        if (this.ExtraForm.get('type').value == 'TICKET'){
            _allprd.setValue(true);
            _allprd.disable();
        }
        else {
            _allprd.enable();
        }
    }

    OnPriceChange(){
        let _value = this.ExtraForm.get('price').value;
        if (_value){
            this.ExtraForm.controls['price'].setValue(parseFloat(_value.toString().replace(",", ".")).toFixed(2));            
        }
        else {
            this.ExtraForm.controls['price'].setValue(null);
        }
    }

    private _selectedtables = null;
    get SelectTables(){
        return this._selectedtables;
    }

    OnShowTables(){
        this._selectedtables = this.ExtraForm.get('tables').value || [];;
        this.change.markForCheck();
    }

    OnHideTables(){
        this._selectedtables = null;
        this.change.markForCheck();
    }

    OnAddTables(tables){
        this.OnHideTables();

        tables.sort((a, b) => {
            return a.number - b.number;
        });

        this.ExtraForm.get('tables').setValue(tables);
        this.ExtraForm.markAsDirty();

        this.change.markForCheck();        
    }

    private _selectedproducts = null;
    get SelectProducts(){
        return this._selectedproducts;
    }    

    OnShowProducts(){
        this._selectedproducts = this.ExtraForm.get('products').value || [];
        this.change.markForCheck();
    }

    OnHideProducts(){
        this._selectedproducts = null;
        this.change.markForCheck();
    }

    OnAddProducts(products){
        this.OnHideProducts();

        this.ExtraForm.get('products').setValue(products);

        this.ExtraForm.markAsDirty();
        this.change.markForCheck();
    }

    OnPeriodChange(valid){
        this.ExtraForm.get('periods').setErrors(valid ? null : { 'periods': true });
        if (valid){
            this.ExtraForm.markAsDirty();
        }
    }

    OnAddPeriod(){
        let _periods = this.ExtraForm.get('periods').value;

        let _period = new ExtraPeriod(null, this.data);
        if (_period){
            _periods.push(_period);
        }

        this.ExtraForm.get('periods').setValue(_periods);
        this.ExtraForm.markAsDirty();

        this.change.markForCheck();
    }

    OnDelPeriod(period){
        let _periods = this.ExtraForm.get('periods').value;
        let _idx = _periods.indexOf(period);
        if (_idx != -1){
            _periods.splice(_idx, 1);
        }

        this.ExtraForm.get('periods').setValue(_periods);
        this.ExtraForm.markAsDirty();

        this.change.markForCheck();
    }

    private _ApplyPeriods(_extra, periods){
        if (_extra.allday){
            for(let _period of _extra.periods){
                _period.status = 'DE';
            }
        }
        else {
            let _periods = [];

            for(let _period of periods){
                _period.extra = _extra;
    
                let _copyof = _period.CopyOf;               
                if (_copyof){
                    _copyof.extra = _period.extra;
                    _copyof.ini = _period.ini;
                    _copyof.end = _period.end;
                    _copyof.weekdays = _period.weekdays;
                    _copyof.timezone = _period.timezone;
    
                    _periods.push(_copyof);
                }
                else {
                    _periods.push(_period);
                }
            }
    
            // include all periods in the final list
            for(let _period of _periods){
                _extra.AddPeriod(_period);
            }
    
            // remove periods not in the final list
            for(let _period of _extra.periods){
                if (_periods.indexOf(_period) == -1){
                    _period.status = 'DE';
                }
            }    
        }
    }

    private _indexOfPlaceTable(needle: QrCode, haystack: Array <ExtraQrCode>){
        for(let _idx = 0; _idx < haystack.length; _idx++){
            if (haystack[_idx].qrcode == needle){
                return _idx;
            }
        }
        return -1;
    }

    private _indexOfExtraTable(needle: ExtraQrCode, haystack: Array <QrCode>){
        for(let _idx = 0; _idx < haystack.length; _idx++){
            if (haystack[_idx] == needle.qrcode){
                return _idx;
            }
        }
        return -1;
    }

    private _ApplyTables(_extra, tables){
        // include all qrcodes in the final list
        for(let _qrcode of tables){
            if (this._indexOfPlaceTable(_qrcode, _extra.tables) == -1){
                let _extraqrcode = new ExtraQrCode(null, this.data);
                if (_extraqrcode){
                    _extraqrcode.qrcode = _qrcode;
                    _extraqrcode.extra = _extra;
                    _extraqrcode.status = 'AC';
                }

                _extra.AddTable(_extraqrcode);
            }
        }

        // remove qrcodes not in the final list
        for(let _extraqrcode of _extra.tables){
            if (this._indexOfExtraTable(_extraqrcode, tables) == -1){
                _extraqrcode.status = 'DE';
            }
        }

        // refresh the tables view
        for(let _extraqrcode of _extra.tables){
            _extraqrcode.qrcode.DoRefresh('EXTRA');
        }
    }

    private _indexOfPlaceProduct(needle: Product, haystack: Array <ExtraProduct>){
        for(let _idx = 0; _idx < haystack.length; _idx++){
            if (haystack[_idx].product == needle){
                return _idx;
            }
        }
        return -1;
    }

    private _indexOfExtraProduct(needle: ExtraProduct, haystack: Array <Product>){
        for(let _idx = 0; _idx < haystack.length; _idx++){
            if (haystack[_idx] == needle.product){
                return _idx;
            }
        }
        return -1;
    }

    private _ApplyProducts(_extra, products){
        if (_extra.allprd){
            for(let _product of _extra.products){
                _product.status = 'DE';
            }
        }
        else {
            let _extraproducts: Array <ExtraProduct> = [];

            // update the extraproduct information (extra)
            for(let _extraproduct of products){
                _extraproduct.extra = _extra;
                _extraproducts.push(_extraproduct);
            }

            // include all products in the final list
            for(let _extraproduct of _extraproducts){
                _extra.AddProduct(_extraproduct);
            }

            // remove products not in the final list
            for(let _extraproduct of _extra.products){
                if (!_extraproducts.includes(_extraproduct)){
                    _extraproduct.status = 'DE';
                }
            }

            // refresh the products view
            for(let _extraproduct of _extra.products){
                _extraproduct.product.DoRefresh('EXTRA');
            }
        }
    }

    private async _CommitExtra(_extra){
        let _transaction = await this.data.BeginTransaction();
        if (_transaction){
            if (_extra){
                this._extra.status = 'DE';
                if (this._extra.ToUpdate){
                    _transaction.SendCommit(this._extra);
                }

                _extra.CopyOf = null;
                _transaction.SendCommit(_extra);

                this._place.AddExtra(_extra);
            }

            return await _transaction.Flush(true);
        }

        return false;
    }

    async OnAccept(){
        let _copy = this._extra.Copy() as Extra;
        if (_copy){
            _copy.photo = this.ExtraForm.get('photo').value;
            if ((!_copy.hasPhoto) || (_copy.photo == this._nophoto)) {
                _copy.photo = _copy.base64 = null;
            }
    
            _copy.name = this.ExtraForm.get('name').value;
            _copy.type = this.ExtraForm.get('type').value;
            _copy.price = this.ExtraForm.get('price').value;
            _copy.description = this.ExtraForm.get('description').value;
            _copy.allday = this.ExtraForm.get('allday').value;
            _copy.allprd = this.ExtraForm.get('allprd').value;
    
            this._ApplyPeriods(_copy, this.ExtraForm.get('periods').value);
            this._ApplyTables(_copy, this.ExtraForm.get('tables').value);
            this._ApplyProducts(_copy, this.ExtraForm.get('products').value)
        }        

        this.ExtraForm.markAsPristine();

        if (await this._CommitExtra(_copy)){
            this.OnClose();
        }
    }

    async OnRemove(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_extra_delete_confirm'),
            message: this.lang.tr('@place_extra_delete_message', [ this._extra.name ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                this._extra.status = 'DE';
                this._extra.DoCommit();

                this.ExtraForm.markAsPristine();
                this.OnClose();
            }
        });

        await alert.present();
    }

    async OnClose(){
        if (this.ExtraForm.dirty){
            console.log("[EDIT EXTRA] confirm changes lost");
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@changes_lost_confirm_tittle'),
                message: this.lang.tr('@changes_lost_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);

                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);

                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnClose();
                }
            });

            await alert.present();        
        }
        else {
            this._OnClose();
        }
    }
}
